import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Modal,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import React, { useState } from "react";
import { useAlert } from "react-alert";

import { AllowListType } from "../../PHIAllowListPage/types";
import FreshpaintUser from "../../components/users/FreshpaintUser";
import Unauthorized from "../../components/users/Unauthorized";
import useCurrentProject from "../../hooks/use-project";
import useAccountMetadata from "../../hooks/useAccountMetadata";
import useUsers from "../../hooks/useUsers";
import { LoadedDestinationInfo, SensitivityLevel } from "../../types/global";
import { makeRequest } from "../../util";
import {
  DestinationPropertyMetadata,
  PropertyGroup,
  getPropertyGroupMetadata,
  getPropertySensitivityLevelsForDestination,
  isPropertyAllowedForDestination,
  isPropertyAllowedForProject,
} from "../DestinationPropertyMetadata";
import { SaaSDestination } from "../destinations";
import { useModalStyles } from "./styles";

interface HIPAAPropertyGroupDestinationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    destination: SaaSDestination,
    id: string,
    newConfig: LoadedDestinationInfo
  ) => void;
  dest: SaaSDestination;
  existingConfig: LoadedDestinationInfo;
  propertyGroup: PropertyGroup;
  title: string;
  content: JSX.Element | null;
}

export function HIPAAPropertyGroupDestinationModal({
  open,
  onClose,
  onSave,
  dest,
  existingConfig,
  propertyGroup,
  title,
  content,
}: HIPAAPropertyGroupDestinationModalProps): JSX.Element | null {
  const alert = useAlert();
  const classes = useModalStyles();
  const usersState = useUsers();
  const accountMetadata = useAccountMetadata();
  const currentProject = useCurrentProject();

  if (!accountMetadata.loaded || !currentProject.loaded) {
    return null;
  }

  // getDisplayRows has only display rows
  const getDisplayRows = (content) => {
    if (content === null) {
      return content;
    }

    let lookupKeys = {};

    const filteredRows: any[] = [];

    content.forEach((element) => {
      if (!(element.property in lookupKeys)) {
        filteredRows.push(element);
      }
      lookupKeys[element.property] = true;
    });

    return filteredRows;
  };

  // propertyMetadataList has all allowlist (metadata) rows
  const propertyMetadataList = getPropertyGroupMetadata(
    dest.key,
    propertyGroup
  );
  if (propertyMetadataList.length === 0) {
    return null;
  }

  // displayedCheckboxPropertyMetadataList has only displayed allowlist (metadata) rows (no dups)
  const displayedCheckboxPropertyMetadataList = getDisplayRows(
    propertyMetadataList
  );

  const [checkedList, setCheckedList] = useState(
    _.map(displayedCheckboxPropertyMetadataList, (propertyMetadata) =>
      isPropertyAllowedForDestination(propertyMetadata, existingConfig)
    )
  );

  const toggleProperty = (targetIndex: number) => {
    setCheckedList(
      _.map(checkedList, (checked, i) => {
        if (i === targetIndex) {
          return !checked;
        } else {
          return checked;
        }
      })
    );
  };

  const handleSave = async (): Promise<void> => {
    const newConfig = _.merge({}, existingConfig);

    // Create distinctNamesLookup for the state of the distinct UI-displayed elements
    let distinctNamesLookup = {};
    displayedCheckboxPropertyMetadataList.forEach((propertyMetadata, i) => {
      distinctNamesLookup[propertyMetadata.property] = checkedList[i];
    });

    // Set the allowlist state for all elements, from the distinctNamesLookup
    //  This includes the scenario where a given prop name is present as both an event and user prop
    propertyMetadataList.forEach((propertyMetadata) => {
      const sensitivityLevels = getPropertySensitivityLevelsForDestination(
        newConfig,
        propertyMetadata.propertyType
      );

      if (distinctNamesLookup[propertyMetadata.property]) {
        sensitivityLevels[propertyMetadata.property] =
          SensitivityLevel.NotSensitive;
      } else {
        delete sensitivityLevels[propertyMetadata.property];
        // This is tricky - handling the case when a property has an alternate,
        // legacy name. If the required properties modal is saved with this
        // property unchecked, we remove the alternate name from the allowlist.
        // But if they save the required properties modal with this property
        // checked, we shouldn't touch the alternate name. This avoids
        // introducing the alternate name to customers who aren't using it, but
        // lets customers who are using the alternate name continue using it
        // unless they actually decide to block the prop.
        if (propertyMetadata.alternateName) {
          delete sensitivityLevels[propertyMetadata.alternateName];
        }
      }
    });

    try {
      await makeRequest("update_destination_phi_allowlist", {
        destination_type: dest.key,
        sensitivity_type: AllowListType.Event,
        sensitivity_levels: newConfig.event_property_sensitivity_levels,
      });
      await makeRequest("update_destination_phi_allowlist", {
        destination_type: dest.key,
        sensitivity_type: AllowListType.User,
        sensitivity_levels: newConfig.user_property_sensitivity_levels,
      });
      await makeRequest("update_destination_phi_allowlist", {
        destination_type: dest.key,
        sensitivity_type: AllowListType.Group,
        sensitivity_levels: newConfig.group_property_sensitivity_levels,
      });

      onSave(dest, existingConfig.id, existingConfig);
      onClose();
    } catch (error: any) {
      alert.error("Something went wrong. Unable to save HIPAA Allow List.");
      console.log(error);
      Sentry.captureException(error);
    }
  };

  const canAccess =
    !accountMetadata.config.rbac ||
    new FreshpaintUser(usersState.currentUser).hasAdminAccess();
  if (!canAccess) {
    return (
      <Modal onClose={onClose} open={open}>
        <Card className={clsx(classes.root, classes.extraSmallModal)}>
          <CardHeader
            title="Unauthorized"
            classes={{ action: clsx(classes.closeIconAction) }}
            action={<CloseIcon onClick={onClose} />}
          />
          <Divider />
          <CardContent>
            <Unauthorized message="Only Admin can view / update HIPAA Allowlist" />
          </CardContent>
        </Card>
      </Modal>
    );
  }

  const renderCheckbox = (
    propertyMetadata: DestinationPropertyMetadata,
    i: number
  ) => {
    const key = `${propertyMetadata.propertyType}_${propertyMetadata.property}`;
    const allowedForProject = isPropertyAllowedForProject(
      propertyMetadata,
      currentProject
    );
    const allowedForDestination = checkedList[i];
    return (
      <FormControl
        key={i}
        component="fieldset"
        className={clsx("my-5", "w-full", classes.formControl)}
      >
        <FormGroup key={i}>
          <FormControlLabel
            key={i}
            label={
              <div>
                <Typography variant="h6">
                  <pre>{propertyMetadata.property}</pre>
                </Typography>
                <Typography variant="body2">
                  {propertyMetadata.description}
                </Typography>
                {allowedForProject && !allowedForDestination && (
                  <Typography variant="body2">
                    <i>
                      This property is globally allowed for the entire{" "}
                      {currentProject.name} project
                    </i>
                  </Typography>
                )}
              </div>
            }
            className={clsx(classes.formControlLabel)}
            control={
              <Checkbox
                className="m-1"
                checked={allowedForDestination}
                indeterminate={allowedForProject && !allowedForDestination}
                onChange={() => toggleProperty(i)}
                name={key}
              />
            }
          />
        </FormGroup>
      </FormControl>
    );
  };

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root, classes.extraSmallModal)}>
        <CardHeader
          title={title}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          {content}
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          {_.map(displayedCheckboxPropertyMetadataList, renderCheckbox)}
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button color="primary" onClick={handleSave} variant="contained">
            Confirm
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}
